import { Fragment, useState } from "react";
import * as dateFns from "date-fns";
import clsx from "clsx";
import { Link } from "@tanstack/react-router";
import {
  AppError,
  Button,
  Icon,
  Loader,
  SidebarOverlay,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
  type TagTone,
  Tooltip,
} from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- TODO: refactor to use a proper component
import type { TFunction } from "i18next";
import { useCurrentLocalization } from "@bfc-software/bfc-web-ui/i18n";

import { fromQuery } from "@bfc-software/bfc-web-ui/lib/async-utils";
import { useTaskDetail } from "../-data/use-task-detail";
import type {
  ActivityEntry,
  PickCapture,
  PickLine,
  PickStatus,
  PickUnit,
  Task,
  TaskDetail,
} from "../-data/types";

import { NotReleasedMark, TaskStatusTag } from "./task-bits";
import {
  TaskActivity,
  toActivityItems,
  type ActivityState,
} from "./task-activity";

/**
 * The task detail drawer: what a task is made of - its meta and its pick lines.
 *
 * Built on pallet's `SidebarOverlay` in its `control: "external"` arm, which
 * renders no trigger of its own. That matters here: this drawer is opened by a
 * table row that may be scrolled out of view by the time it closes, and which
 * task is open is a URL fact owned by `useTaskDetailDrawer` - so a
 * `DialogTrigger`'s internal state would be a second, competing answer to "is
 * it open". Under the external arm there is no such state to compete: the hook
 * stays the only source of truth, a reload or a pasted link still reopens the
 * drawer, and this component is mounted only for a selected row (hence the
 * constant `isOpen`), with every dismissal - Escape, the close button, a click
 * on the scrim - routed straight back out through `onClose`.
 *
 * The panel, its scrim, the header row and the close button are
 * `SidebarOverlay`'s now; what stays here is what this drawer puts inside them.
 */

/**
 * The three path params any link out of this drawer needs, handed down from
 * whichever tab rendered it rather than read here - the drawer is not a route
 * and has no `Route.useParams()` of its own.
 */
export interface WarehouseParams {
  lang: string;
  org: string;
  location: string;
}

/** `startedAt` bounds a claim time, not a shelf-life window, but the same
 * unparseable-falls-back-to-raw guard applies for the same reason. */
function formatStarted(value: string): string {
  const parsed = dateFns.parseISO(value);
  return dateFns.isValid(parsed) ? dateFns.format(parsed, "PP p") : value;
}

/**
 * Expiry is a date, not a moment (§11 R2): `PP`-formatted from the UTC
 * calendar date, never the local one. `parseISO` gives back a `Date` that
 * `format` would otherwise read in the browser's own zone, so
 * `2026-09-01T00:00:00Z` would print "Aug 31, 2026" west of UTC. Rebuilding a
 * local `Date` from the UTC Y/M/D fields makes `format` agree with the wire
 * value everywhere the drawer runs.
 */
function formatExpiryDate(value: string): string {
  const parsed = dateFns.parseISO(value);
  if (!dateFns.isValid(parsed)) return value;
  const utcCalendarDate = new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate(),
  );
  return dateFns.format(utcCalendarDate, "PP");
}

/*
 * HOW THIS FILE CONSUMES PALLET. It is the shape the outbound routes are being
 * moved to, so it is worth stating once here rather than at each element.
 *
 * 1. Pallet components first. The pick lines table is `Table` and friends, not
 *    a hand-rolled `<table>` with utilities on every cell. It was the latter
 *    until SR-342: the drawer arrived in SR-156, before SR-301 existed, and
 *    nothing revisited it. A bespoke table is not a small local choice -- it is
 *    a second answer to what a table row looks like, and it drifts from the one
 *    every other outbound screen renders.
 *
 * 2. No CSS module. `task-detail-drawer.module.css` is gone: every rule in it
 *    either had a pallet T2 class already (`.content` -> `drawer-content`) or
 *    was a handful of T1 utilities spelled as raw `var(--pallet-*)`
 *    declarations, which is the debt SR-301 set out to remove rather than a
 *    shape pallet was missing.
 *
 * 3. Classes are written at the element that wears them -- inline in the
 *    `className`, or composed with `clsx` where something is conditional. Not
 *    hoisted into `const *_CLASSES` tables at the top of the file: reading the
 *    JSX should tell you what it renders without jumping elsewhere and back.
 *    Every one is checked against pallet's real entry CSS at lint time by
 *    `better-tailwindcss/no-unknown-classes`, which reaches `className`
 *    attributes and `clsx()` arguments out of the box, so a typo'd or retired
 *    utility is an `npm run eslint` error rather than a silent no-op.
 *
 * 4. Where a pallet component has no vocabulary for what a call site needs, the
 *    utilities go on an element INSIDE it (the `font-mono` span in the slot
 *    cell), never on a `className` override reaching at its chrome. That keeps
 *    the gap visible as a gap. The fix for a gap worth closing is a prop on the
 *    pallet component, in pallet -- not a local override here.
 */

/** One meta grid cell: an uppercase label over its value. */
function MetaField({
  label,
  value,
  className,
}: {
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx("flex min-w-0 flex-col", className)}>
      <span className="section-heading">{label}</span>
      {/* Not `truncate`: that adds `white-space: nowrap`, which the module this
          replaced deliberately did not set -- a long plan name wraps to a
          second line here and only then clips. */}
      <span className="mt-4 overflow-hidden text-ellipsis text-size-14 text-on-surface">
        {value}
      </span>
    </div>
  );
}

/**
 * Where a pick line's item id goes when clicked: the warehouse's Items page,
 * with this one item searched for.
 *
 * The items list has no per-item route to deep link to - the id goes into its
 * `q`, which is an exact `item_ext_id` lookup for anything shaped like an item
 * id and a description substring search otherwise (see `searchFilterParam` in
 * `items.tsx`). Every id here comes from `TaskDetailPick.item_id`, which *is*
 * that catalog id, so the search lands on the one row.
 *
 * `attrs` and `offset` are stated rather than left out: the items route's
 * search is a whole object, and carrying over an attribute filter or a page
 * offset from wherever the user last was would land them on a filtered page
 * two that may not hold the item at all.
 */
function ItemLink({
  params,
  itemId,
}: {
  params: WarehouseParams;
  itemId: string;
}) {
  return (
    <Link
      to="/$lang/orgs/$org/outbound/whs/$location/items"
      params={params}
      search={{ q: itemId, attrs: "", offset: 0 }}
      // The dotted rule is `border-b` + `border-dotted` + `border-outline`
      // rather than the module's `border-bottom: 1px dotted ...` shorthand:
      // only the bottom edge has a width, so naming the style and the colour on
      // all four sides renders identically, and it lets the hover/focus arms
      // restate the colour alone.
      className="border-b border-dotted border-outline font-mono font-strong text-on-surface no-underline hover:border-primary hover:text-primary focus-visible:border-primary focus-visible:text-primary"
      data-test-role="task-detail-pick-item-link"
    >
      {itemId}
    </Link>
  );
}

/**
 * How each pick status reads as a tone.
 *
 * Exhaustive over `PickStatus`, for the reason `STATUS_TONES` in
 * `task-bits.tsx` is exhaustive over `TaskStatus`: a member added to the union
 * fails here rather than rendering as an untoned chip nobody notices.
 *
 * `short` is `warning` and not `danger`. A short is a normal, expected outcome
 * of a pick - the item was not there in the quantity the plan asked for - and
 * the row is not an error to be fixed. `danger` in this palette is reserved for
 * something that went wrong, which is the tone the unknown case below carries.
 */
const PICK_STATUS_TONES: Record<PickStatus, TagTone> = {
  pending: "neutral",
  "in-progress": "info",
  picked: "success",
  short: "warning",
};

/** The i18n key under `tasks.taskDetail.drawer.pickStatus` for each status. */
const PICK_STATUS_LABEL_KEYS: Record<PickStatus, string> = {
  pending: "pending",
  "in-progress": "inProgress",
  picked: "picked",
  short: "short",
};

/**
 * The Status cell: a chip for a pick we have an answer for, an icon affordance
 * for the two kinds we do not.
 *
 * `unknown` and `not-released` are both drawn as icon + `Tooltip` + hidden
 * label rather than as a chip, and for the same reason `NotReleasedMark` in
 * `task-bits.tsx` states: every chip in this column is a claim about the
 * warehouse, and neither of these is one. But the two must not look alike -
 * `unknown` keeps `AlertCircle` in `danger`, because it means "something went
 * wrong" (the history read failed, or came back with no row for this pick, on
 * a task that *was* released), while `not-released` gets `MinusCircle`, the
 * same glyph `NotReleasedMark` draws in the table's Status column, because it
 * means "nothing has happened yet" and is not an error to flag. Confusing the
 * two would tell a manager a planned task's picks are broken, or a released
 * task's failed read is merely unstarted. Not `NotReleasedMark` itself,
 * though: that component's copy is the table's, and this cell's strings live
 * under `tasks.taskDetail.drawer.pickStatus` instead - same shape, own
 * wording.
 */
function PickStatusCell({ pick }: { pick: PickLine }) {
  const [t] = useCurrentLocalization();
  if (pick.execution.kind === "not-released") {
    return (
      <span
        // Muted rather than `danger` - the opposite reasoning from the unknown
        // arm below, and deliberately so: a planned task's pick has not gone
        // wrong, it simply has not been released yet, and painting it the same
        // red as a failed read would tell a manager the wrong one of those two
        // stories.
        className="inline-flex text-on-surface-variant"
        data-test-role="task-detail-pick-status"
        data-status="not-released"
      >
        <Tooltip content={t("tasks.taskDetail.drawer.pickStatus.notReleased")}>
          <Icon name="MinusCircle" size={16} />
          <span className="sr-only">
            {t("tasks.taskDetail.drawer.pickStatus.notReleasedLabel")}
          </span>
        </Tooltip>
      </span>
    );
  }

  if (pick.execution.kind === "unknown") {
    return (
      <span
        // `danger` rather than a muted role: this is the one thing in the table
        // that is wrong rather than merely unfinished, and a grey glyph in a
        // column of coloured chips reads as another status.
        className="inline-flex text-danger"
        data-test-role="task-detail-pick-status"
        data-status="unknown"
      >
        <Tooltip content={t("tasks.taskDetail.drawer.pickStatus.unknown")}>
          <Icon name="AlertCircle" size={16} />
          <span className="sr-only">
            {t("tasks.taskDetail.drawer.pickStatus.unknownLabel")}
          </span>
        </Tooltip>
      </span>
    );
  }

  const status = pick.execution.status;

  return (
    // Tag stamps its own data-test-role, so the cell-level hook goes on a
    // wrapper - same arrangement as `TaskStatusTag`.
    <span data-test-role="task-detail-pick-status" data-status={status}>
      <Tag
        label={t(
          `tasks.taskDetail.drawer.pickStatus.${PICK_STATUS_LABEL_KEYS[status]}`,
        )}
        display={{ tone: PICK_STATUS_TONES[status] }}
      />
    </span>
  );
}

/**
 * The Picked Qty cell's value and tone, off `pick.execution` (§11 Q1/Q8):
 * no fulfillment (pending/in-progress/not-released/unknown) is an empty span,
 * a terminal fulfillment carries `pickedQty` already joined by step 1, and
 * `short` alone renders in warning tone.
 */
function pickedQtyView(pick: PickLine): {
  value: number | null;
  tone: "muted" | "warning" | null;
} {
  if (pick.execution.kind !== "known") {
    return { value: null, tone: null };
  }
  return {
    value: pick.execution.pickedQty,
    tone: pick.execution.status === "short" ? "warning" : "muted",
  };
}

/** A line's per-unit capture rows, off `pick.execution` (§11 Q2) - empty when
 * there is no fulfillment to read, same as `pickedQtyView` above. */
function pickUnits(pick: PickLine): PickUnit[] {
  return pick.execution.kind === "known" ? pick.execution.units : [];
}

/**
 * One capture slot's value (§11 Q2, global no-dash rule): `"absent"` ->
 * localized "N/A", `"not-confirmed"` -> muted "Not captured", `"captured"` ->
 * the formatted value. `format` handles the one kind-specific rendering rule
 * (catchweight's verbatim `${value} ${uom}`, expiry's `PP`-formatted date);
 * everything else renders its value raw.
 */
function renderPickCapture<T>(
  capture: PickCapture<T>,
  t: TFunction,
  format: (value: T) => string = (value) => String(value),
): React.ReactNode {
  switch (capture.kind) {
    case "absent":
      return t("tasks.taskDetail.drawer.pickUnits.notApplicable");
    case "not-confirmed":
      return (
        <span className="text-on-surface-variant">
          {t("tasks.taskDetail.drawer.pickUnits.notCaptured")}
        </span>
      );
    case "captured":
      return format(capture.value);
  }
}

/** One row of the pick lines table. */
function PickLineRow({
  pick,
  params,
  expanded,
  onToggle,
}: {
  pick: PickLine;
  params: WarehouseParams;
  expanded: boolean;
  onToggle: (id: string) => void;
}) {
  const [t] = useCurrentLocalization();
  const { value: pickedQty, tone: pickedQtyTone } = pickedQtyView(pick);
  const canExpand = pickUnits(pick).length > 0;

  return (
    <TableRow data-test-role="task-detail-pick-row">
      <TableCell>
        {canExpand ? (
          // `Button kind="icon-only" variant="bare"` - the same shape
          // `plan-card.tsx:91-100` (`variant="tertiary"`) and
          // `user-groups.tsx:633-644` (`variant="bare"`) already use for a row
          // toggle; `bare` fits here since `TableRow`'s own hover state
          // already gives the cell its affordance.
          <Button
            kind="icon-only"
            variant="bare"
            icon={expanded ? "ChevronDown" : "ChevronRight"}
            label={t("tasks.taskDetail.drawer.table.toggleUnits", {
              seq: pick.seq,
            })}
            aria-expanded={expanded}
            aria-controls={`task-detail-pick-units-${pick.id}`}
            onPress={() => onToggle(pick.id)}
            data-test-role="task-detail-pick-toggle"
          />
        ) : null}
      </TableCell>
      <TableCell>{pick.seq}</TableCell>
      <TableCell>
        <ItemLink params={params} itemId={pick.itemId} />
        {pick.itemName ? (
          <div className="text-size-12 text-on-surface-variant">
            {pick.itemName}
          </div>
        ) : (
          <></>
        )}
      </TableCell>
      {/*
        No em-dash when there is none, unlike the slot column beside it: a pick
        snapshotted before the service began stamping zones carries none and
        never backfills (§4), so an absent zone says when the plan was made,
        not that anything is missing from this pick.
      */}
      <TableCell>{pick.zoneName ?? ""}</TableCell>
      {/* The one cell that still says something about its own type. `TableCell`
          has no vocabulary for "this column is machine text", so the utility
          goes on a span INSIDE the cell rather than on the cell's own
          `className` -- see the note above `DrawerBody`. */}
      <TableCell>
        <span className="font-mono">{pick.slotId ?? "—"}</span>
      </TableCell>
      <TableCell>
        {pick.quantity} {pick.uom}
      </TableCell>
      <TableCell>
        {/* Empty span rather than a dash when null (global no-dash rule,
            §11) - `pick.pickedQty` is a `number | null`, not a pre-formatted
            string. */}
        <span
          className={
            pickedQtyTone === "warning" ? "font-mono text-warning" : "font-mono"
          }
          data-test-role="task-detail-picked-qty"
        >
          {pickedQty}
        </span>
      </TableCell>
      {/* Right-aligned, as in the design: the chips are the last thing down the
          row and a ragged left edge on a column of varying-width tags scans
          worse than a ragged right one against the table's own edge. The header
          above now agrees, which it did not before - the module asked for a
          right-aligned `<th>` and lost the declaration to a specificity clash
          (`.pickTable th` (0,1,1) over `.thStatus` (0,1,0)), so the column
          shipped with a left-aligned header over right-aligned cells. */}
      <TableCell align="right">
        <PickStatusCell pick={pick} />
      </TableCell>
    </TableRow>
  );
}

/**
 * The expanded sub-row's content, one `TableRow` immediately under its line's
 * row, one cell spanning the whole table. Columns are always the fixed 4
 * (`#`, lot, expiration, weight) - no `columns` prop, no `GRID_COLS` lookup
 * (§11 Q2).
 *
 * A real pallet `Table` rather than the grid div this used to be (§11 R2): it
 * nests cleanly inside the parent `TableCell` (a `<table>` inside a `<td>` is
 * ordinary HTML, and each `Table` instance owns its own react-aria collection
 * state, so the two do not contend over context), and `display.tableLayout`
 * defaults to `"auto"` - content-width columns for free, with no per-call
 * override needed. Its own `overflow-x-auto` wrapper scrolls this table
 * sideways independent of the parent's, same reasoning as R1 below: its
 * columns needn't line up with the parent's (engineer's call).
 */
function PickUnitsRow({
  lineId,
  seq,
  units,
}: {
  lineId: string;
  seq: number;
  units: PickUnit[];
}) {
  const [t] = useCurrentLocalization();
  return (
    <TableRow
      className="bg-surface-container"
      data-test-role="task-detail-pick-units-row"
    >
      <TableCell colSpan={8}>
        <div
          id={`task-detail-pick-units-${lineId}`}
          className="overflow-x-auto rounded-10 border border-outline"
        >
          <Table
            aria-label={t("tasks.taskDetail.drawer.pickUnits.tableLabel", {
              seq,
            })}
            isResizable={false}
          >
            <TableHead>
              <TableHeaderCell isRowHeader={true}>
                {t("tasks.taskDetail.drawer.table.unitSeq")}
              </TableHeaderCell>
              <TableHeaderCell>
                {t("tasks.taskDetail.drawer.pickUnits.column.lot")}
              </TableHeaderCell>
              <TableHeaderCell>
                {t("tasks.taskDetail.drawer.pickUnits.column.expiry")}
              </TableHeaderCell>
              <TableHeaderCell>
                {t("tasks.taskDetail.drawer.pickUnits.column.catchweight")}
              </TableHeaderCell>
            </TableHead>
            <TableBody>
              {units.map((u) => (
                <TableRow key={u.index}>
                  <TableCell>
                    <span className="font-mono text-on-surface-variant">
                      {u.index + 1}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono">
                      {renderPickCapture(u.lot, t)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono">
                      {renderPickCapture(u.expiry, t, formatExpiryDate)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono">
                      {renderPickCapture(
                        u.catchweight,
                        t,
                        (value) => `${value.value} ${value.uom}`,
                      )}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * The body once `TaskDetail` has loaded: the meta grid, the pick lines table
 * and - for an activated task - the Activity timeline.
 *
 * `ListTaskHistory` carries every device action as a `TaskPickEvent` with its
 * own `DeviceAction` payload; `task-activity.tsx` is the vocabulary this file
 * once lacked to render one. It no longer draws a line per device action
 * (§11 R3 supersedes Q3/Q6's per-`DeviceAction`-kind labels): it groups a pick
 * line's `kind: "pick"` events into one "Pick" entry, joined to `picks` here
 * for its `#seq itemId`, and reads only enough of each grouped event's
 * `DeviceAction` to decide the group's dot tone. Every lifecycle arm's
 * `selector_id` still resolves to a name via the page-wide `selectorNames`
 * directory (§11 Q4). A planned task shows none of it (§11 Q5): there is no
 * history to read before a task is released.
 */
function DrawerBody({
  task,
  picks,
  activity,
  selectorNames,
  params,
}: {
  task: Task;
  picks: PickLine[];
  activity: ActivityEntry[] | null;
  selectorNames: ReadonlyMap<string, string>;
  params: WarehouseParams;
}) {
  const [t] = useCurrentLocalization();
  const [expandedLineIds, setExpandedLineIds] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const toggleLine = (id: string) =>
    setExpandedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  // `activity === null` covers both "planned, never asked" and "history read
  // failed" (§9.3's `TaskDetail.activity` docblock); only an activated task's
  // failure gets the `unavailable` affordance (§11 Q5) - a planned task shows
  // no Activity section at all.
  const activityState: ActivityState =
    activity === null
      ? { kind: "unavailable" }
      : activity.length === 0
        ? { kind: "empty" }
        : {
            kind: "loaded",
            entries: toActivityItems(activity, picks, selectorNames, t),
          };

  return (
    // The scroll parent. Marked for the suite because "does this scroll" is a
    // question about THIS box specifically -- `drawer-body` owns the
    // `overflow-y: auto` -- and reaching for a pallet class name from a test
    // would tie the assertion to pallet's spelling rather than to the drawer.
    <div className="drawer-body" data-test-role="task-detail-body">
      {/* Equal thirds, 16px gutters (18 is not a step on pallet's spacing scale). */}
      <div className="flex">
        <MetaField
          className="flex-1"
          label={t("tasks.taskDetail.drawer.plan")}
          value={task.planName}
        />
        <MetaField
          className="flex-1 ml-16"
          label={t("tasks.taskDetail.drawer.route")}
          value={
            task.routeCodes.length > 0
              ? task.routeCodes.join(", ")
              : t("tasks.row.none")
          }
        />
        {/* Blank rather than "Unassigned" when nobody holds it, the same rule
            the Selector column follows (SR-342 item 10). Both surfaces or
            neither: a drawer that says "Unassigned" over a blank cell is the
            two disagreeing about the same task. */}
        <MetaField
          className="flex-1 ml-16"
          label={t("tasks.taskDetail.drawer.selector")}
          value={
            task.kind === "activated"
              ? (task.selectorName ?? "")
              : t("tasks.row.none")
          }
        />
      </div>
      <div className="flex mt-16">
        <MetaField
          className="flex-1"
          label={t("tasks.taskDetail.drawer.lines")}
          value={task.lineCount}
        />
        {/*
          Selector, Progress and Started each render the shared em-dash for a
          planned task: none of the three is a field `Task`'s `planned` arm
          carries (see its docblock in `types.ts`) - there is no holder, no
          done-pick count and no claim time to report for a task nobody has
          released, and drawing "0/n" or "Not started" here would answer those
          questions with a value rather than admit there is none yet.
        */}
        <MetaField
          className="flex-1 ml-16"
          label={t("tasks.taskDetail.drawer.progress")}
          value={
            task.kind === "activated"
              ? `${task.picksDone}/${task.lineCount}`
              : t("tasks.row.none")
          }
        />
        <MetaField
          className="flex-1 ml-16"
          label={t("tasks.taskDetail.drawer.started")}
          value={
            task.kind === "activated"
              ? task.startedAt
                ? formatStarted(task.startedAt)
                : t("tasks.taskDetail.drawer.notStarted")
              : t("tasks.row.none")
          }
        />
      </div>

      <div className="section-heading">
        {t("tasks.taskDetail.drawer.pickLinesHeading")}
      </div>
      {/*
        `flex-none` is the fix for SR-342's "the drawer's pick table does not
        scroll".

        The wrapper used to be `overflow-hidden`, there only to clip the
        rounded corners against the rows -- but ANY non-`visible` overflow
        makes a flex item's cross-axis default to an AUTOMATIC MINIMUM SIZE of
        0. So on a task with more picks than fit, this wrapper shrank to
        whatever `drawer-body` had left and clipped its own rows, instead of
        growing past the drawer and handing the overflow up to `drawer-body`'s
        `overflow-y: auto`. Nothing ever overflowed, so no scrollbar appeared
        and the clipped rows were simply unreachable: measured live at 24 picks,
        a 1382px table inside a 606px wrapper with the scroll parent reporting
        `scrollHeight === clientHeight`.

        `flex-none` (`flex: none` -> `flex-shrink: 0`) takes the shrink away
        regardless of what the wrapper's own overflow says, so the wrapper
        stays its content's height and `drawer-body` scrolls -- held by the "a
        long task's last pick line is reachable" case in
        `outbound-tasks.spec.ts`.

        `overflow-x-auto` (§11 R1, replacing `overflow-hidden`) is what makes
        the swap safe: this wrapper has too many columns to fit at some
        widths, and `overflow-hidden` was clipping the Status column instead
        of scrolling to it, the same "clipped, scrollbar-less and simply
        unreachable" failure `flex-none` fixed for the vertical axis. Setting
        only the x axis leaves `overflow-y` to compute to `auto` alongside it
        (pallet's `table-frame` note, `utilities.css:409`) -- inert here
        specifically BECAUSE `flex-none` already pins this box to its content
        height: with shrinking disallowed, the box's own height never falls
        short of its content, so `overflow-y: auto` never finds anything of
        its own to scroll and the overflow keeps going to `drawer-body`, same
        as before. This is the "parent that has a [flex-shrunk-away] height"
        case that note calls out as the one where the pairing is NOT inert;
        `flex-none` is exactly what removes that parent's shrink, which is why
        it stays inert here.
      */}
      <div className="flex-none overflow-x-auto rounded-10 border border-outline">
        <Table
          aria-label={t("tasks.taskDetail.drawer.pickLinesHeading")}
          // Same as the task table's, and for the same reason: nothing here is
          // wide enough to need dragging, and the resizable arm wraps the table
          // in a `ResizableTableContainer` whose own `overflow: hidden` would
          // sit between this wrapper and the rows.
          isResizable={false}
        >
          <TableHead>
            {/* Disclosure column - no header label, mirrors the toggle's own
                cell having no visible text either. */}
            <TableHeaderCell />
            <TableHeaderCell>
              {t("tasks.taskDetail.drawer.table.seq")}
            </TableHeaderCell>
            {/* The row header is the item, not the sequence number beside it:
                this is the cell a screen reader repeats to say which pick a
                given column belongs to, and "ITEM-4417" answers that where "3"
                does not. It need not be the first column. */}
            <TableHeaderCell isRowHeader={true}>
              {t("tasks.taskDetail.drawer.table.item")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("tasks.taskDetail.drawer.table.zone")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("tasks.taskDetail.drawer.table.slot")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("tasks.taskDetail.drawer.table.qty")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("tasks.taskDetail.drawer.table.pickedQty")}
            </TableHeaderCell>
            <TableHeaderCell align="right">
              {t("tasks.taskDetail.drawer.table.status")}
            </TableHeaderCell>
          </TableHead>
          <TableBody>
            {picks.map((pick) => {
              const units = pickUnits(pick);
              const expanded = expandedLineIds.has(pick.id);
              return (
                <Fragment key={pick.id}>
                  <PickLineRow
                    pick={pick}
                    params={params}
                    expanded={expanded}
                    onToggle={toggleLine}
                  />
                  {expanded && units.length > 0 ? (
                    <PickUnitsRow
                      lineId={pick.id}
                      seq={pick.seq}
                      units={units}
                    />
                  ) : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {task.kind === "activated" ? (
        <>
          <div className="section-heading">
            {t("tasks.taskDetail.drawer.activityHeading")}
          </div>
          <TaskActivity state={activityState} />
        </>
      ) : null}
    </div>
  );
}

export function TaskDetailDrawer({
  planId,
  taskId,
  task,
  params,
  onClose,
  selectorNames,
}: {
  planId: string;
  taskId: number;
  /** The route params links out of the drawer are built from. */
  params: WarehouseParams;
  task: Task;
  onClose: () => void;
  /** Page-wide selector ULID -> display name directory (§11 Q4), threaded to
   * `TaskActivity` for the "by" line. */
  selectorNames: ReadonlyMap<string, string>;
}) {
  const [t] = useCurrentLocalization();
  const state = fromQuery<TaskDetail>(useTaskDetail({ planId, taskId, task }));

  return (
    <SidebarOverlay
      control="external"
      // Constant, for the reason the docblock above gives: the caller renders
      // this only for the task the URL names, so that selection is the single
      // answer to "is it open" and `onOpenChange` routes every dismissal back
      // to it.
      isOpen={true}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
      // The status chip beside the task id is this screen's content, not chrome
      // `SidebarOverlay` could supply, so it composes into `header`.
      header={
        <>
          <div className="flex items-center">
            <h2 className="drawer-title min-w-0 truncate">
              {t("tasks.taskDetail.drawer.title", { id: task.id })}
            </h2>
            <div className="flex-none ml-8">
              {task.kind === "activated" ? (
                <TaskStatusTag status={task.status} />
              ) : (
                <NotReleasedMark />
              )}
            </div>
          </div>
          <div className="drawer-subtitle">
            {task.routeCodes.length > 0
              ? task.routeCodes.join(", ")
              : t("tasks.row.none")}
          </div>
        </>
      }
    >
      {/* The drawer's test role sits on its contents rather than on the panel
          element, which is `SidebarOverlay`'s now and takes no props of ours.
          Everything the suites reach for through it - the pick rows, their
          item links, the loading and failure states - is inside.

          TODO: `SidebarOverlay` has no way for a caller to mark the panel or
          its close button for a test harness, which is why the close button is
          reached by its accessible name in `outbound-tasks.spec.ts`. A
          pass-through for those two is worth considering once more than one
          drawer wants it - out of scope here. */}
      <div className="drawer-content" data-test-role="task-detail-drawer">
        {(() => {
          switch (state.kind) {
            case "loading":
              return (
                <div
                  className="flex flex-1 items-center justify-center p-24"
                  data-test-role="task-detail-loading"
                >
                  <Loader type="inline" />
                </div>
              );
            case "failed":
              return (
                <div
                  className="flex flex-1 items-center justify-center p-24"
                  data-test-role="task-detail-failed"
                >
                  <AppError
                    description={t("tasks.taskDetail.drawer.failed")}
                    resolution={t("tasks.failureSupport")}
                  />
                </div>
              );
            case "loaded":
              return (
                <DrawerBody
                  task={task}
                  picks={state.value.picks}
                  activity={state.value.activity}
                  selectorNames={selectorNames}
                  params={params}
                />
              );
          }
        })()}
      </div>
    </SidebarOverlay>
  );
}
